class ProductStickyButton extends HTMLElement {
  #intersectionObserver = null;
  #stickyElement = null;
  #originalButton = null;
  #productTitle = null;
  #isMobile = false;
  #section = null;

  connectedCallback() {
    this.#isMobile = window.innerWidth <= 749;
    this.#section = this.closest('.shopify-section, dialog');
    if (this.#section) {
      this.#section.addEventListener('variant:update', this.#handleVariantUpdate);
    }

    if (!this.#isMobile) return;

    this.#originalButton = this.querySelector('.product-form-buttons');
    if (!this.#originalButton) return;

    this.#productTitle = this.#findProductTitle();
    this.#createStickyElement();
    this.#setupIntersectionObserver();
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
    this.#section?.removeEventListener('variant:update', this.#handleVariantUpdate);
  }

  #handleVariantUpdate = (event) => {
    const productForm = this.closest('product-form-component');
    const eventProductId = event.detail?.data?.productId;
    const formProductId = productForm?.dataset?.productId;
    if (!event.detail?.data?.newProduct && eventProductId && formProductId && eventProductId !== formProductId) {
      return;
    }
    const newTitle = this.#findProductTitle();
    if (newTitle && this.#stickyElement) {
      const titleEl = this.#stickyElement.querySelector('.product-sticky-button-mobile__title');
      if (titleEl) titleEl.textContent = newTitle;
    }
    if (!this.#stickyElement || !this.#isMobile) return;
    // Defer so product-form has already updated the main add-to-cart
    requestAnimationFrame(() => this.#syncStickyButtons());
  };

  #syncStickyButtons() {
    if (!this.#originalButton || !this.#stickyElement || !this.#isMobile) return;
    const oldButtons = this.#stickyElement.querySelector('.product-sticky-button-mobile__buttons');
    if (!oldButtons) return;
    const newClone = this.#originalButton.cloneNode(true);
    const submitBtn = this.#prepareButtonsClone(newClone);
    oldButtons.replaceWith(newClone);
    if (submitBtn) submitBtn.addEventListener('click', this.#handleStickySubmit);
  }

  #handleStickySubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const mainSubmit = this.#originalButton?.querySelector('button[type="submit"]');
    if (mainSubmit) mainSubmit.click();
    else this.closest('form')?.requestSubmit();
  };

  /**
   * @param {Element} clone
   * @returns {HTMLButtonElement | null}
   */
  #prepareButtonsClone(clone) {
    clone.className = 'product-sticky-button-mobile__buttons';
    const qty = clone.querySelector('.quantity-selector, quantity-selector-component');
    if (qty) qty.remove();
    const btn = clone.querySelector('button[type="submit"]');
    if (btn?.id) btn.id = btn.id + '-sticky';
    clone.querySelectorAll('[ref], [on\\:click], [on\\:submit]').forEach((el) => {
      el.removeAttribute('ref');
      el.removeAttribute('on:click');
      el.removeAttribute('on:submit');
    });
    return btn;
  }

  #handleResize = () => {
    const wasMobile = this.#isMobile;
    this.#isMobile = window.innerWidth <= 749;
    
    if (wasMobile && !this.#isMobile) {
      this.#deactivateStickyButton();
    } else if (!wasMobile && this.#isMobile) {
      this.#activateStickyButton();
    }
  };

  #activateStickyButton() {
    if (this.#stickyElement) {
      this.#setupIntersectionObserver();
      return;
    }

    this.#originalButton = this.querySelector('.product-form-buttons');
    if (!this.#originalButton) return;

    this.#productTitle = this.#findProductTitle();
    this.#createStickyElement();
    this.#setupIntersectionObserver();
  }

  #deactivateStickyButton() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    if (this.#stickyElement) {
      this.#stickyElement.remove();
      this.#stickyElement = null;
    }
  }

  #findProductTitle() {
    const productDetails = document.querySelector('.product-details');
    if (productDetails) {
      const h1 = productDetails.querySelector('h1');
      if (h1) {
        const text = h1.textContent.trim();
        if (text && text.length > 0) {
          return text;
        }
      }
      
      const viewTitle = productDetails.querySelector('.view-product-title');
      if (viewTitle) {
        const link = viewTitle.querySelector('a');
        if (link) {
          const text = link.textContent.trim();
          if (text && text.length > 0) {
            return text;
          }
        }
        const text = viewTitle.textContent.trim();
        if (text && text.length > 0) {
          return text;
        }
      }
      
      const productTitleBlock = productDetails.querySelector('[class*="product-title"]');
      if (productTitleBlock) {
        const text = productTitleBlock.textContent.trim();
        if (text && text.length > 0) {
          return text;
        }
      }
      
      const headings = productDetails.querySelectorAll('h1, h2, h3');
      for (const heading of headings) {
        const text = heading.textContent.trim();
        if (text && text.length > 0 && text.length < 200) {
          return text;
        }
      }
    }
    
    const metaTitle = document.querySelector('meta[property="og:title"]');
    if (metaTitle) {
      const content = metaTitle.getAttribute('content');
      if (content) {
        return content;
      }
    }
    
    if (document.title) {
      return document.title;
    }
    
    return null;
  }

  #createStickyElement() {
    this.#stickyElement = document.createElement('div');
    this.#stickyElement.className = 'product-sticky-button-mobile';

    if (this.#productTitle) {
      const titleElement = document.createElement('div');
      titleElement.className = 'product-sticky-button-mobile__title';
      titleElement.textContent = this.#productTitle;
      this.#stickyElement.appendChild(titleElement);
    }

    const buttonClone = this.#originalButton.cloneNode(true);
    const clonedButton = this.#prepareButtonsClone(buttonClone);
    this.#stickyElement.appendChild(buttonClone);
    this.#originalButton.parentNode.insertBefore(this.#stickyElement, this.#originalButton.nextSibling);

    if (clonedButton) {
      clonedButton.addEventListener('click', this.#handleStickySubmit);
    }
  }

  #setupIntersectionObserver() {
    if (!this.#originalButton || !this.#stickyElement) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const rect = entry.boundingClientRect;
        if (!entry.isIntersecting && rect.top < 0) {
          this.#stickyElement.classList.add('is-sticky');
        } 
        else if (entry.isIntersecting || rect.top > window.innerHeight) {
          this.#stickyElement.classList.remove('is-sticky');
        }
      });
    }, options);

    this.#intersectionObserver.observe(this.#originalButton);
  }
}

if (!customElements.get('product-sticky-button')) {
  customElements.define('product-sticky-button', ProductStickyButton);
}
